import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';

const SCANNER_ACCENT = '#04c2ff';

const normalizeScanner = (scannerSerialNumber) => {
    const trimmedScannerSerialNumber = scannerSerialNumber?.trim();

    return trimmedScannerSerialNumber || 'N/A';
};

const buildChartRows = (rows) => {
    const sortedRows = [
        ...rows.filter((row) => row.scanner_serial_number?.trim()),
        ...rows.filter((row) => !row.scanner_serial_number?.trim()),
    ];
    const maxCaseCount = sortedRows.reduce(
        (acc, row) => Math.max(acc, row.case_count || 0),
        0,
    );

    return sortedRows.map((row, index) => ({
        barWidth: maxCaseCount > 0 ? `${(row.case_count / maxCaseCount) * 100}%` : '0%',
        caseCount: row.case_count || 0,
        key: row.scanner_serial_number || `unknown-scanner-${index}`,
        rank: index + 1,
        scannerSerialNumber: normalizeScanner(row.scanner_serial_number),
    }));
};

function ScanVolumeByScannerChart({ analytics }) {
    const rows = analytics?.rows || [];
    const chartRows = buildChartRows(rows);
    const hasRows = rows.length > 0;

    return (
        <section className="dashboard-chart-card dashboard-chart-card--clinic">
            <div className="dashboard-chart-header dashboard-chart-header--with-icon">
                <h2>Scan Volume by Scanner</h2>
                <Tooltip title="The number of scans taken by individual scanners for the selected date range.">
                    <InfoOutlinedIcon className="dashboard-chart-info-icon" fontSize="small" />
                </Tooltip>
            </div>
            <div className={`dashboard-chart-body${hasRows ? ' dashboard-chart-body--auto' : ''}`}>
                {hasRows ? (
                    <div className="dashboard-clinic-chart">
                        <div className="dashboard-clinic-chart__header">
                            <span>Scanner</span>
                            <span aria-hidden="true" />
                            <span>Total Cases</span>
                        </div>
                        <div className="dashboard-clinic-chart__rows">
                            {chartRows.map((row) => (
                                <div key={row.key} className="dashboard-clinic-chart__row">
                                    <div className="dashboard-clinic-chart__clinic-cell">
                                        <span className="dashboard-clinic-chart__rank">{row.rank}</span>
                                        <span className="dashboard-clinic-chart__name">{row.scannerSerialNumber}</span>
                                    </div>
                                    <div className="dashboard-clinic-chart__bar-cell">
                                        <div
                                            className="dashboard-clinic-chart__bar"
                                            style={{
                                                backgroundColor: SCANNER_ACCENT,
                                                width: row.barWidth,
                                            }}
                                        />
                                    </div>
                                    <div className="dashboard-clinic-chart__count">{row.caseCount}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dashboard-chart-empty">
                        <p>No data for the selected date range.</p>
                        <p>Try expanding the date range or adjusting filters.</p>
                    </div>
                )}
            </div>
        </section>
    );
}

ScanVolumeByScannerChart.propTypes = {
    analytics: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.shape({
            case_count: PropTypes.number,
            scanner_serial_number: PropTypes.string,
        })),
    }),
};

ScanVolumeByScannerChart.defaultProps = {
    analytics: null,
};

export default ScanVolumeByScannerChart;
